import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchGalleryImages } from '../store/slices/gallerySlice';
import axios from '../utils/axiosConfig';
import ServicesSection from './pages/ServicesSection';
import GalleryCarousel from '../components/pages/GalleryCarrusel';
import VenueSection from '../components/pages/VenueSection';
import VenueAvailability from './pages/VenueAvailability';
import EditableTitle from '../components/pages/EditableTitle';
import EventsCarouselOne from './events/EventsCarouselOne';
import TournamentsCarouselOne from './events/TournamentsCarouselOne';
import Hero from './layout/Hero';
import { fetchTournaments } from '../store/slices/tournamentSlice';
import { isEventUpcoming } from '../utils/date';

const Home = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [cliente, setCliente] = useState(null);
  const [loadingCliente, setLoadingCliente] = useState(true);
  const [errorCliente, setErrorCliente] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const deleteMode = false;
  const [rrppStatus, setRrppStatus] = useState(null);
  const [rrppMessage, setRrppMessage] = useState('');

  const galleryState = useSelector(state => state.gallery);
  const { isAuthenticated, user } = useSelector(state => state.auth);
  const isAdminHere = isAuthenticated && cliente && (
    (user?.role === 'admin' && user?.clientId === cliente._id) || 
    (user?.memberships?.some(m => m.clientId && m.clientId.toString() === cliente._id.toString() && m.role === 'admin'))
  );
  const galleryImages = Array.isArray(galleryState.images) ? galleryState.images : [];
  const errorGallery = galleryState.error;

  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [venueImages, setVenueImages] = useState([]);
  const { tournaments } = useSelector((s) => s.tournaments);

  const upcomingEvents = React.useMemo(() => {
    return events.filter(ev => isEventUpcoming(ev.date));
  }, [events]);

  const upcomingTournaments = React.useMemo(() => {
    return (tournaments || []).filter(t => t.status !== 'Finalizado' && isEventUpcoming(t.endDate || t.startDate));
  }, [tournaments]);
  const organizationType = cliente?.organizationType;
  const isCreatorSpace = organizationType === 'creator';
  const hasPhysicalVenue = organizationType === 'venue' || organizationType === 'sports';

  useEffect(() => {
    dispatch(fetchGalleryImages({ slug, category: 'event' }));
    dispatch(fetchTournaments(slug));
  }, [dispatch, slug]);

  useEffect(() => {
    const fetchCliente = async () => {
      try {
        const resp = await axios.get(`/api/clients/${slug}`);
        setCliente(resp.data);
        setLoadingCliente(false);
      } catch (err) {
        if (err.response && err.response.status === 404) {
          navigate('/', { replace: true });
        } else {
          setErrorCliente('Hubo un error al cargar el cliente.');
          setLoadingCliente(false);
        }
      }
    };
    fetchCliente();
  }, [slug, navigate]);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const resp = await axios.get(`/${slug}/api/events`);
        setEvents(resp.data);
      } catch (err) {
        console.error('Error al cargar eventos', err);
      } finally {
        setLoadingEvents(false);
      }
    };
    fetchEvents();
  }, [slug]);

  useEffect(() => {
    const fetchVenueImages = async () => {
      try {
        const { data } = await axios.get(`/${slug}/api/images`, { params: { category: 'venue' } });
        setVenueImages(Array.isArray(data?.images) ? data.images : []);
      } catch {
        setVenueImages([]);
      }
    };
    fetchVenueImages();
  }, [slug]);

  useEffect(() => {
    if (isAuthenticated && !isAdminHere) {
      axios.get(`/${slug}/api/rrpp/my-status`)
        .then(resp => setRrppStatus(resp.data.relationship))
        .catch(err => console.error('Error checking RRPP status', err));
    }
  }, [isAuthenticated, isAdminHere, slug]);

  const handleRegisterRrpp = async () => {
    try {
      await axios.post(`/${slug}/api/rrpp/register`);
      setRrppMessage('¡Solicitud enviada! El administrador te contactará.');
      setRrppStatus({ status: 'pending' });
    } catch (err) {
      setRrppMessage('Error al enviar solicitud.');
    }
  };

  const saveTitles = async (patch) => {
    try {
      await axios.put(`/api/clients/titles/${slug}`, patch, { withCredentials: true });
      setCliente((c) => {
        const next = { ...c };
        if (patch.welcomeTitle !== undefined) next.texts = { ...next.texts, welcomeTitle: patch.welcomeTitle };
        if (patch.upcomingTitle !== undefined) next.texts = { ...next.texts, upcomingTitle: patch.upcomingTitle };
        if (patch.servicesTitle !== undefined) next.texts = { ...next.texts, servicesTitle: patch.servicesTitle };
        if (patch.welcomeDescription !== undefined) next.description = patch.welcomeDescription;
        return next;
      });
    } catch (e) {
      console.error('No se pudo guardar títulos', e);
    }
  };

  const handleDelete = async (imageId) => {
    try {
      setDeletingId(imageId);
      await axios.delete(`/${slug}/api/images/${imageId}`, { withCredentials: true });
      dispatch(fetchGalleryImages({ slug, category: 'event' }));
    } catch (err) {
    } finally {
      setDeletingId(null);
    }
  };

  if (loadingCliente && !cliente) {
    return (
      <div className="flex items-center justify-center h-screen bg-background text-center px-4">
        <div className="flex flex-col items-center gap-6">
          <div className="w-16 h-16 border-4 border-primary-orange/10 border-t-primary-orange rounded-full animate-spin"></div>
          <div>
            <h2 className="text-2xl font-black text-white mb-2">PASSAR</h2>
            <p className="text-white/40 font-bold uppercase tracking-[3px] text-[10px] animate-pulse">Cargando experiencia premium...</p>
          </div>
        </div>
      </div>
    );
  }

  if (errorCliente || errorGallery) {
    const mensajeError = typeof errorCliente === 'string' ? errorCliente : 'Ocurrió un error al cargar la página.';
    return (
      <div className="flex items-center justify-center h-screen bg-background px-6">
        <div className="text-center p-12 rounded-[40px] border border-red-500/20 bg-red-500/5 backdrop-blur-xl">
          <p className="text-red-400 font-bold text-lg mb-6">{mensajeError}</p>
          <button onClick={() => window.location.reload()} className="px-8 py-3 bg-brand-gradient text-white rounded-2xl font-black uppercase tracking-widest text-[10px] shadow-xl shadow-orange-500/20 active:scale-95 transition-all">Reintentar</button>
        </div>
      </div>
    );
  }

  const firstVenueImage = venueImages[0]?.url || venueImages[0] || '';
  const firstEventImage = upcomingEvents[0]?.coverUrl || upcomingEvents[0]?.image || upcomingEvents[0]?.coverImageUrl || '';
  const heroImage = hasPhysicalVenue ? firstVenueImage || cliente?.logoUrl : firstEventImage || cliente?.logoUrl;

  return (
    <div className="pb-24">

      {/* Hero Section Impactante */}
      <Hero
        cliente={cliente}
        isAdmin={isAdminHere}
        coverImage={heroImage}
        upcomingCount={cliente?.organizationType === 'sports' ? upcomingTournaments.length : upcomingEvents.length}
        onSaveTitle={(val) => saveTitles({ welcomeTitle: val })}
        onSaveDescription={(val) => saveTitles({ welcomeDescription: val })}
        onExplore={() => {
          const eventsSec = document.getElementById(cliente?.organizationType === 'sports' ? 'torneos' : 'proximos-eventos');
          if (eventsSec) eventsSec.scrollIntoView({ behavior: 'smooth' });
        }}
        onBrowseSpace={hasPhysicalVenue && venueImages.length > 0 ? () => {
          document.getElementById('instalaciones')?.scrollIntoView({ behavior: 'smooth' });
        } : undefined}
        onRequestQuote={organizationType === 'venue' ? () => {
          document.getElementById('disponibilidad')?.scrollIntoView({ behavior: 'smooth' });
        } : undefined}
      />

      {organizationType === 'venue' ? <VenueAvailability slug={slug} /> : null}

      {cliente?.organizationType === 'sports' ? (
        <>
          {/* PRIMERO TORNEOS */}
          {upcomingTournaments && upcomingTournaments.length > 0 && (
            <section id="torneos" className="text-center mb-40 scroll-mt-24">
              <div className="mb-16">
                <h2 className="text-4xl md:text-6xl font-black text-white mb-4 tracking-tighter">
                  <span className="text-gradient">Torneos</span>
                </h2>
                <div className="w-24 h-1.5 bg-brand-gradient mx-auto rounded-full"></div>
              </div>
              <div className="animate-fade-in-up">
                <TournamentsCarouselOne tournaments={upcomingTournaments} slug={slug} color={cliente?.colores?.primario} autoPlay={true} autoPlayMs={4500} />
              </div>
            </section>
          )}
          {/* LUEGO EVENTOS (Solo si no es 'sports' y ademas de torneos hace eventos, pero por definicion 'sports' no deberia. Lo quitamos por pedido del user) */}
        </>
      ) : (
        <>
          {/* PRIMERO EVENTOS (Layout clásico) */}
          <section id="proximos-eventos" className="mb-24 scroll-mt-24">
            <div className="mb-10 flex flex-col justify-between gap-5 border-b border-white/10 pb-6 sm:flex-row sm:items-end">
              <div>
                <p className="mb-3 text-[11px] font-black uppercase tracking-[2px] text-primary-orange">Agenda</p>
                <EditableTitle as="h2" className="text-3xl font-black text-white sm:text-4xl" value={cliente?.texts?.upcomingTitle} placeholder="Próximos eventos" disabled={!isAdminHere} onSave={(val) => saveTitles({ upcomingTitle: val })} />
              </div>
              {upcomingEvents.length > 0 ? <p className="text-sm font-semibold text-white/45">{upcomingEvents.length} publicados</p> : null}
            </div>
            {loadingEvents && !upcomingEvents.length ? (
              <div className="flex flex-col items-center py-20 gap-4">
                <div className="w-12 h-12 border-4 border-primary-orange/10 border-t-primary-orange rounded-full animate-spin"></div>
                <p className="text-[10px] font-black text-white/20 uppercase tracking-[2px]">Sincronizando cartelera...</p>
              </div>
            ) : upcomingEvents.length === 0 ? (
              <div className="border-y border-white/10 py-12">
                <p className="text-base font-medium text-white/45">Todavía no hay eventos publicados.</p>
              </div>
            ) : (
              <div className="animate-fade-in-up">
                <EventsCarouselOne events={upcomingEvents} slug={slug} color={cliente?.colores?.primario} autoPlay={true} autoPlayMs={4000} />
              </div>
            )}
          </section>

          {/* LUEGO TORNEOS */}
          {upcomingTournaments && upcomingTournaments.length > 0 && (
            <section id="torneos" className="text-center mb-40 scroll-mt-24">
              <div className="mb-16">
                <h2 className="text-4xl md:text-6xl font-black text-white mb-4 tracking-tighter">
                  Torneos <span className="text-gradient">Destacados</span>
                </h2>
                <div className="w-24 h-1.5 bg-brand-gradient mx-auto rounded-full"></div>
              </div>
              <div className="animate-fade-in-up">
                <TournamentsCarouselOne tournaments={upcomingTournaments} slug={slug} color={cliente?.colores?.primario} autoPlay={true} autoPlayMs={4500} />
              </div>
            </section>
          )}
        </>
      )}

      {/* Servicios */}


      {/* Servicios */}
      {!isCreatorSpace && (
        <div id="servicios" className="mb-40 scroll-mt-24">
          <ServicesSection
            cliente={cliente}
            isAdmin={isAdminHere}
          />
        </div>
      )}

      {/* Instalaciones y Galería */}
      {hasPhysicalVenue && (
        <section id="instalaciones" className="mb-24 md:mb-40 scroll-mt-24">
          <VenueSection
            slug={slug}
            cliente={cliente}
            isAdmin={isAdminHere}
          />

          <div className="mt-20 md:mt-32">
            <div className="text-center mb-10 md:mb-16">
              <h2 className="text-4xl md:text-6xl font-black text-white mb-4 tracking-tighter leading-none">
                Momentos <span className="text-gradient">Inolvidables</span>
              </h2>
              <div className="w-24 h-1.5 bg-brand-gradient mx-auto rounded-full"></div>
            </div>

            {galleryImages.length === 0 ? (
              <div className="text-center py-10 opacity-30 italic">
                <p className="text-white">Todavía no hay fotos en la galería.</p>
              </div>
            ) : (
              <div className="animate-fade-in-up">
                <GalleryCarousel
                  images={galleryImages}
                  deleteMode={deleteMode}
                  deletingId={deletingId}
                  onDelete={(id) => handleDelete(id)}
                  isAdmin={isAdminHere}
                  autoPlay={true}
                  autoPlayMs={3000}
                />
              </div>
            )}
          </div>
        </section>
      )}

      {/* Únete como RRPP (Condicional según orgType y eventos) */}
      {!isAdminHere && isAuthenticated && (cliente?.organizationType !== 'sports' || events.length > 0) && (
        <section className="mt-40 p-12 md:p-20 bg-surface rounded-[60px] border border-white/5 shadow-2xl relative overflow-hidden group">
          <div className="absolute top-0 left-0 w-full h-1 bg-brand-gradient transition-all duration-700 group-hover:h-2"></div>
          <div className="absolute -bottom-20 -right-20 w-80 h-80 bg-primary-orange/5 blur-[100px] rounded-full"></div>

          <div className="relative z-10 flex flex-col items-center text-center">
            <h3 className="text-4xl md:text-5xl font-black text-white mb-6 tracking-tight leading-none">
              ¿Querés ser RRPP oficial?
            </h3>
            <p className="text-white/40 mb-12 max-w-2xl text-lg font-medium leading-relaxed">
              Formá parte del equipo de <span className="text-white">{cliente?.nombre}</span>. Vendé entradas para nuestros eventos y ganá comisiones por cada venta confirmada.
            </p>

            {rrppStatus ? (
              <div className="inline-flex flex-col items-center gap-4 px-10 py-6 bg-white/5 border border-white/10 rounded-3xl backdrop-blur-xl animate-fade-in-up">
                <p className="font-black uppercase tracking-widest text-[11px] text-white/60">
                  Estado de tu solicitud:
                </p>
                <div className="text-lg font-bold text-white">
                  {rrppStatus.status === 'pending' ? '⌛ Pendiente de Aprobación' :
                    rrppStatus.status === 'approved' ? '✅ Ya sos RRPP oficial' : '❌ Solicitud rechazada'}
                </div>
                {rrppStatus.status === 'approved' && (
                  <Link to={`/${slug}/rrpp/dashboard`} className="text-primary-orange hover:underline font-black uppercase tracking-widest text-[10px] mt-2">
                    Ir a tu Panel →
                  </Link>
                )}
              </div>
            ) : (
              <button
                onClick={handleRegisterRrpp}
                className="group relative px-12 py-5 bg-brand-gradient text-white font-black uppercase tracking-[3px] text-xs rounded-2xl shadow-2xl shadow-orange-500/30 hover:scale-105 active:scale-95 transition-all overflow-hidden"
              >
                <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300"></div>
                <span className="relative z-10">Postularme Ahora</span>
              </button>
            )}

            {rrppMessage && <p className="mt-8 font-black text-primary-orange uppercase tracking-widest text-[10px] animate-bounce">{rrppMessage}</p>}
          </div>
        </section>
      )}
    </div>
  );
};

export default Home;
